import { useState } from "react";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Callout,
  Card,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  PageIntro,
  useToast,
} from "../components/ui.jsx";
import { IconPlus, IconRefresh } from "../components/icons.jsx";
import { kindLabel } from "./Dashboard.jsx";

const KINDS = [
  { value: "BRANCH", label: "Branch: household service point" },
  { value: "BRANCH_PLUS", label: "Branch+: productive use service point" },
  { value: "NODEFLEX", label: "NodeFlex: generation and storage hub" },
  { value: "NODENET", label: "NodeNet: distribution node" },
  { value: "POWERSHARE", label: "PowerShare: energy sharing controller" },
  { value: "NOVALINK", label: "NovaLink: data concentrator (gateway)" },
];

export default function Models() {
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => api.listModels(), []);
  const [creating, setCreating] = useState(false);

  const models = data ?? [];

  return (
    <>
      <PageIntro title="Hardware models">
        A model describes a hardware variant and supplies the defaults every unit built
        from it inherits: how many metered channels it exposes, how many phases it has and
        what load it is rated for. Register the model before the production run.
      </PageIntro>

      <div className="toolbar">
        <span className="spacer" />
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          <IconRefresh /> Refresh
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          <IconPlus /> New model
        </button>
      </div>

      {loading ? (
        <Loading label="Loading models" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : models.length === 0 ? (
        <Card>
          <Empty
            title="No models registered"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <IconPlus /> Register the first model
              </button>
            }
          >
            Nothing can be manufactured until at least one hardware model exists.
          </Empty>
        </Card>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Product kind</th>
                <th>Direct connections</th>
                <th>Feeds</th>
                <th>Phases</th>
                <th>Rated for</th>
                <th>Gateway capacity</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.Code}>
                  <td className="mono">{m.Code}</td>
                  <td>{kindLabel(m.Kind)}</td>
                  <td>
                    {m.ChannelCount === 0 ? (
                      <span className="hint">Meters nothing</span>
                    ) : m.ChannelCount > 1 ? (
                      `${m.ChannelCount}-in-1`
                    ) : (
                      "One"
                    )}
                  </td>
                  <td>
                    {m.BranchCapacity ? (
                      `${m.BranchCapacity} service points`
                    ) : (
                      <span className="hint">Nothing downstream</span>
                    )}
                  </td>
                  <td>{m.Phases === 3 ? "Three" : "Single"}</td>
                  <td>{m.MaxPowerWatts ? `${m.MaxPowerWatts} W` : <span className="hint">-</span>}</td>
                  <td>{m.GatewayCapacity || <span className="hint">Not a gateway</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateModel
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
            toast.success("Model registered");
          }}
        />
      )}
    </>
  );
}

// Capacity is a property of the hardware, so it is declared once here and
// inherited by every unit built from the model. Nothing at registration time
// can widen it.
const SUPPLY_KINDS = new Set(["NODENET", "NODEFLEX", "POWERSHARE"]);

function CreateModel({ onClose, onCreated }) {
  const [form, setForm] = useState({
    Code: "",
    Kind: "BRANCH",
    ChannelCount: 1,
    BranchCapacity: 12,
    SourceCount: 4,
    Phases: 1,
    MaxPowerWatts: 2000,
    GatewayCapacity: 0,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isGateway = form.Kind === "NOVALINK";
  const supplies = SUPPLY_KINDS.has(form.Kind);
  const combinesSources = form.Kind === "POWERSHARE";

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createModel({
        Code: form.Code.toUpperCase().trim(),
        Kind: form.Kind,
        // A NovaLink meters nothing, so it has no customer connections at
        // all. A node carries at most the one taken directly off it.
        ChannelCount: isGateway ? 0 : supplies ? 1 : Number(form.ChannelCount),
        BranchCapacity: supplies ? Number(form.BranchCapacity) : 0,
        SourceCount: combinesSources ? Number(form.SourceCount) : 0,
        Phases: Number(form.Phases),
        MaxPowerWatts: isGateway ? 0 : Number(form.MaxPowerWatts),
        GatewayCapacity: isGateway ? Number(form.GatewayCapacity) : 0,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Register a hardware model"
      hint="Capacity is fixed here and inherited by every unit built from it"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" form="create-model" disabled={busy || !form.Code}>
            {busy ? <span className="spinner" /> : null}
            Register model
          </button>
        </>
      }
    >
      <form id="create-model" onSubmit={submit}>
        {error && <Callout kind="warning">{error}</Callout>}

        <Field label="Model code" required hint="A short identifier such as BRANCH-4C-2K.">
          <input
            className="input mono"
            value={form.Code}
            onChange={(e) => setForm({ ...form, Code: e.target.value.toUpperCase() })}
            required
          />
        </Field>

        <Field label="Product kind" required>
          <select
            className="select"
            value={form.Kind}
            onChange={(e) => setForm({ ...form, Kind: e.target.value })}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>

        {isGateway ? (
          <Field
            label="Gateway capacity"
            required
            hint="How many devices this concentrator can carry. An over-subscribed gateway degrades for every device beneath it, and the symptom looks like radio interference rather than a configuration error."
          >
            <input
              className="input"
              type="number"
              min="1"
              value={form.GatewayCapacity}
              onChange={(e) => setForm({ ...form, GatewayCapacity: e.target.value })}
              required
            />
          </Field>
        ) : (
          <>
            {supplies ? (
              <div className="field-row">
                <Field
                  label="Service points it feeds"
                  required
                  hint="How many Branch units this node can carry. Loading it beyond this is not a data-entry problem; it is the node tripping on a hot afternoon."
                >
                  <input
                    className="input"
                    type="number"
                    min="1"
                    value={form.BranchCapacity}
                    onChange={(e) => setForm({ ...form, BranchCapacity: e.target.value })}
                    required
                  />
                </Field>
                {combinesSources && (
                  <Field
                    label="Nodes it combines"
                    required
                    hint="A PowerShare joins several nodes into one supply."
                  >
                    <input
                      className="input"
                      type="number"
                      min="2"
                      value={form.SourceCount}
                      onChange={(e) => setForm({ ...form, SourceCount: e.target.value })}
                      required
                    />
                  </Field>
                )}
              </div>
            ) : (
              <Field
                label="Direct customer connections"
                required
                hint="More than one makes this an n-in-1 unit. Each connection gets its own number and its own token key."
              >
                <input
                  className="input"
                  type="number"
                  min="1"
                  max="99"
                  value={form.ChannelCount}
                  onChange={(e) => setForm({ ...form, ChannelCount: e.target.value })}
                  required
                />
              </Field>
            )}

            <div className="field-row">
              <Field label="Phases" required>
                <select
                  className="select"
                  value={form.Phases}
                  onChange={(e) => setForm({ ...form, Phases: e.target.value })}
                >
                  <option value={1}>Single phase</option>
                  <option value={3}>Three phase</option>
                </select>
              </Field>
              <Field
                label="Rated for (W)"
                required
                hint="The hardware rating. A site can be commissioned below this and never above it."
              >
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={form.MaxPowerWatts}
                  onChange={(e) => setForm({ ...form, MaxPowerWatts: e.target.value })}
                  required
                />
              </Field>
            </div>
          </>
        )}

        {supplies && (
          <Callout kind="info">
            A node also carries one direct customer connection, for the household wired
            straight to it. That connection uses the node's own number and is vended to
            like any other service point.
          </Callout>
        )}

        {!isGateway && !supplies && Number(form.ChannelCount) > 1 && (
          <Callout kind="info">
            Each channel receives its own customer-facing number, derived from the device
            number, and its own token key. Vend against the channel number, not the device
            number.
          </Callout>
        )}
      </form>
    </Modal>
  );
}
